import { ScaleElement } from '@scale/base-element';

const OVA_PREVIEW_ATTRIBUTE = 'data-ova-preview';
const OVA_PREVIEW_OPEN_ATTRIBUTE = 'data-ova-popup-preview-open';
const OVA_PREVIEW_MODAL_ATTRIBUTE = 'data-ova-popup-preview-modal';

const DAY_MS = 86400000;

class ScalePopup extends ScaleElement {
  static requiredRefs = ['dialog'];

  #previousFocus;
  #triggered = false;

  setup() {
    const dialog = this.ref('dialog');

    dialog.addEventListener('close', () => this.#onDialogClose(), { signal: this.signal });
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog && !this.#isCorner) this.close();
    }, { signal: this.signal });

    this.on('click', (event) => {
      if (event.target === this && this.hasAttribute(OVA_PREVIEW_MODAL_ATTRIBUTE)) this.close();
    });

    this.on('scale:newsletter:subscribed', () => this.#write(localStorage, this.#key('converted'), 'true'));

    if (this.#isOvaPreview()) {
      this.#disableAutomaticPreviewOpen(dialog);
      return;
    }

    this.#watchForOvaPreview(dialog);

    this.onDocument('keydown', (event) => {
      if (event.key === 'Escape' && dialog.open && !dialog.matches(':modal')) this.close();
    });

    if (this.#restoreMinimized()) return;
    if (!this.#shouldShow()) return;

    this.#registerTrigger();
    this.#setupDesignMode();
  }

  get #isCorner() {
    return this.ref('dialog').classList.contains('popup__dialog--corner');
  }

  open() {
    if (this.#isOvaPreview()) return;
    this.#openDialog();
  }

  openInOvaPreview() {
    if (!this.#isOvaPreview()) return;
    this.setAttribute(OVA_PREVIEW_OPEN_ATTRIBUTE, '');
    this.#openDialog();
  }

  #openDialog() {
    const dialog = this.ref('dialog');
    if (dialog.open) return;

    this.#previousFocus = document.activeElement;

    const isCorner = this.#isCorner;
    const isExplicitOvaPreview = this.#isOvaPreview()
      && this.hasAttribute(OVA_PREVIEW_OPEN_ATTRIBUTE);

    if (isExplicitOvaPreview) {
      this.toggleAttribute(OVA_PREVIEW_MODAL_ATTRIBUTE, !isCorner);
      dialog.show();
    } else if (isCorner) {
      dialog.show();
    } else {
      dialog.showModal();
      this.trapFocus(dialog);
    }

    this.#recordShown();
    this.emit('scale:popup:open', { sectionId: this.dataset.sectionId, trigger: this.getAttribute('data-trigger') });
  }

  close() {
    const dialog = this.ref('dialog');
    this.#resetOvaPreviewPresentation();
    if (dialog.open) dialog.close();
  }

  #onDialogClose() {
    this.#resetOvaPreviewPresentation();
    this.#previousFocus?.focus?.();
    this.emit('scale:popup:close', { sectionId: this.dataset.sectionId });
  }

  dismiss() {
    if (this.getBoolean('data-minimize')) {
      this.#write(sessionStorage, this.#key('minimized'), 'true');
      this.close();
      this.ref('fab').hidden = false;
      return;
    }

    this.#write(localStorage, this.#key('dismissed'), 'true');
    this.close();
  }

  reopen() {
    this.#remove(sessionStorage, this.#key('minimized'));
    this.ref('fab').hidden = true;
    this.open();
  }


  #disableAutomaticPreviewOpen(dialog) {
    this.#resetOvaPreviewPresentation();
    if (dialog.open) dialog.close();

    const observer = new MutationObserver(() => {
      if (dialog.open && !this.hasAttribute(OVA_PREVIEW_OPEN_ATTRIBUTE)) {
        dialog.close();
      }
    });
    observer.observe(dialog, { attributes: true, attributeFilter: ['open'] });
    this.signal.addEventListener('abort', () => observer.disconnect());
  }

  #watchForOvaPreview(dialog) {
    const markerObserver = new MutationObserver(() => {
      if (!this.#isOvaPreview()) return;
      markerObserver.disconnect();
      this.#disableAutomaticPreviewOpen(dialog);
    });
    markerObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: [OVA_PREVIEW_ATTRIBUTE],
    });
    this.signal.addEventListener('abort', () => markerObserver.disconnect());
  }

  #isOvaPreview() {
    return document.documentElement.getAttribute(OVA_PREVIEW_ATTRIBUTE) === 'true';
  }

  #resetOvaPreviewPresentation() {
    this.removeAttribute(OVA_PREVIEW_OPEN_ATTRIBUTE);
    this.removeAttribute(OVA_PREVIEW_MODAL_ATTRIBUTE);
  }


  #key(suffix) {
    return `scale:popup:${this.dataset.sectionId}:${suffix}`;
  }

  #read(store, key) {
    try {
      return store.getItem(key);
    } catch {
      return null;
    }
  }

  #write(store, key, value) {
    if (this.#isOvaPreview()) return;
    try {
      store.setItem(key, value);
    } catch {
    }
  }

  #remove(store, key) {
    try {
      store.removeItem(key);
    } catch {
    }
  }

  #restoreMinimized() {
    if (this.#read(sessionStorage, this.#key('minimized')) !== 'true') return false;

    const fab = this.ref('fab');
    if (!fab) return false;

    fab.hidden = false;
    return true;
  }

  #shouldShow() {
    if (this.getBoolean('data-test-mode')) return true;

    if (this.#read(localStorage, this.#key('converted')) === 'true') return false;
    if (this.#read(localStorage, this.#key('dismissed')) === 'true') return false;
    if (!this.getBoolean('data-show-on-mobile') && this.isMobile) return false;

    const lastShown = parseInt(this.#read(localStorage, this.#key('shown')), 10);
    if (!lastShown) return true;

    switch (this.getAttribute('data-frequency')) {
      case 'once':
        return false;
      case 'session':
        return !this.#read(sessionStorage, this.#key('shown'));
      case 'custom':
        return (Date.now() - lastShown) / DAY_MS >= this.getNumber('data-frequency-days', 7);
      default:
        return true;
    }
  }

  #recordShown() {
    if (this.#isOvaPreview() || this.getBoolean('data-test-mode')) return;

    this.#write(localStorage, this.#key('shown'), Date.now().toString());
    this.#write(sessionStorage, this.#key('shown'), '1');
  }


  #registerTrigger() {
    let trigger = this.getAttribute('data-trigger');

    if (trigger === 'exit-intent' && this.isMobile) {
      trigger = 'delay';
    }

    switch (trigger) {
      case 'delay':
        this.#initDelay();
        break;
      case 'scroll':
        this.#initScroll();
        break;
      case 'exit-intent':
        this.#initExitIntent();
        break;
    }
  }

  #initDelay() {
    const timer = setTimeout(() => this.#fire(), this.getNumber('data-delay', 5000));
    this.signal.addEventListener('abort', () => clearTimeout(timer));
  }

  #initScroll() {
    const threshold = this.getNumber('data-scroll-percent', 50);
    const handler = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      if ((window.scrollY / scrollable) * 100 < threshold) return;

      this.#fire();
      window.removeEventListener('scroll', handler);
    };
    window.addEventListener('scroll', handler, { passive: true, signal: this.signal });
  }

  #initExitIntent() {
    document.documentElement.addEventListener('mouseleave', (event) => {
      if (event.clientY < 0) this.#fire();
    }, { signal: this.signal });
  }

  #fire() {
    if (this.#isOvaPreview() || this.#triggered) return;
    this.#triggered = true;
    this.open();
  }


  #setupDesignMode() {
    if (!Shopify?.designMode) return;

    const sectionId = this.dataset.sectionId;

    this.onDocument('shopify:section:select', (e) => {
      if (e.detail?.sectionId === sectionId) {
        this.#triggered = false;
        this.open();
      }
    });

    this.onDocument('shopify:section:deselect', (e) => {
      if (e.detail?.sectionId === sectionId) this.close();
    });

    if (this.getBoolean('data-test-mode')) {
      setTimeout(() => this.#fire(), 300);
    }
  }
}

if (!customElements.get('scale-popup')) {
  customElements.define('scale-popup', ScalePopup);
}
